/**
 * «Рассылки» → «Напоминание о примерке»: накануне пишем записанным на завтра — по дню ОТПРАВКИ,
 * в какой канал сколько напоминаний ушло, сколько клиентов подтвердили и сколько отменили запись
 * кнопкой в боте, сколько пришли в салон в день записи. Только бесплатные каналы, WhatsApp не шлём.
 * Спека `docs/specs/2026-10-06-mailing-remind-design.md`; числа — как у
 * `prokat-runner mailing-remind --stats`.
 */
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { iso, plusDays } from "@/lib/dates";
import { loadMailingRemind, type RemindCounts } from "@/lib/mailingRemind";

const CHANNEL: Record<string, { label: string; hint: string }> = {
  telegram: { label: "Бот Telegram", hint: "клиент запустил @unirenterbot; с кнопками «Подтвердить» и «Отменить»" },
  max: { label: "Бот MAX", hint: "клиент запустил бота MAX; с кнопками «Подтвердить» и «Отменить»" },
  vk: { label: "ВКонтакте", hint: "клиент писал сообществу за последние 90 дней; без кнопки" },
  avito: { label: "Авито", hint: "клиент писал в Авито за последние 90 дней; без кнопки" },
  instagram: { label: "Instagram", hint: "клиент писал меньше суток назад; без кнопки" },
  other: { label: "Канал неизвестен", hint: "отправка была, но площадка не вернула номер сообщения" },
};
const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const ZERO: RemindCounts = { sent: 0, confirmed: 0, cancelled: 0, came: 0 };

function pct(a: number, b: number): string {
  return a && b ? `${Math.round((a * 100) / b)}%` : "";
}

function dm(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

/** Четыре ячейки счёта: отправлено · подтвердили · отменили · пришли; доли — от отправленных. */
function Cells({ c, last }: { c: RemindCounts; last?: boolean }) {
  const td = "whitespace-nowrap px-3 py-1.5 tabular-nums";
  const mark = (v: number, tone: string) => (
    <>
      <span className={`text-[14px] font-semibold ${v ? tone : "text-fg-faint"}`}>{v}</span>
      {v > 0 && <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, c.sent)}</span>}
    </>
  );
  return (
    <>
      <td className={td}>
        <span className={`text-[14px] font-semibold ${c.sent ? "text-fg" : "text-fg-faint"}`}>{c.sent}</span>
      </td>
      <td className={td}>{mark(c.confirmed, "text-ok")}</td>
      <td className={td}>{mark(c.cancelled, "text-err")}</td>
      <td className={`${td} ${last ? "" : "border-r border-border"}`}>{mark(c.came, "text-ok")}</td>
    </>
  );
}

function Head({ th, last }: { th: string; last: boolean }) {
  return (
    <>
      <th className={th} title="напоминаний отправлено">
        Отпр.
      </th>
      <th className={th} title="клиент нажал «Подтвердить примерку» в боте">
        Подтвердили
      </th>
      <th className={th} title="клиент нажал «Отменить примерку» и подтвердил «Да, отменить» — сделка ушла в «В работе»">
        Отменили
      </th>
      <th className={`${th} ${last ? "" : "border-r border-border"}`} title="приход в салон в день записи">
        Пришли
      </th>
    </>
  );
}

export function RemindTab() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -29));
  const to = params.get("to") || iso(today);
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({ queryKey: ["mailing-remind", from, to], queryFn: () => loadMailingRemind(from, to) });
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const r = q.data;
  // Колонки — только каналы, куда за период что-то ушло.
  const channels = r ? r.channels.filter((k) => (r.total.channels[k]?.sent ?? 0) > 0) : [];
  const all = r?.total.all ?? ZERO;

  return (
    <section className="flex flex-col gap-3" data-annot="напоминание о примерке">
      <div className="flex flex-wrap items-center gap-3" data-annot="период напоминаний о примерке">
        <DateRangePicker label="Отправлено" from={from} to={to} onChange={(a, b) => set({ from: a, to: b })} />
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">Считаю рассылку…</div>}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(q.error.message)}</div>}
      {r && (
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-[12.5px] text-fg-muted" data-annot="итог напоминаний о примерке">
          <span>
            Отправлено <b className="text-[15px] text-fg">{all.sent}</b>
          </span>
          <span title="клиент нажал «Подтвердить примерку» в боте Telegram или MAX">
            подтвердили <b className={`text-[15px] ${all.confirmed ? "text-ok" : "text-fg-faint"}`}>{all.confirmed}</b>{" "}
            {pct(all.confirmed, all.sent)}
          </span>
          <span title="клиент нажал «Отменить примерку» и подтвердил «Да, отменить»">
            отменили кнопкой <b className={`text-[15px] ${all.cancelled ? "text-err" : "text-fg-faint"}`}>{all.cancelled}</b>{" "}
            {pct(all.cancelled, all.sent)}
          </span>
          <span title="приход в салон в день записи">
            пришли <b className={`text-[15px] ${all.came ? "text-ok" : "text-fg-faint"}`}>{all.came}</b> {pct(all.came, all.sent)}
          </span>
        </div>
      )}
      {r && r.days.length === 0 && (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-4 py-3 text-[12.5px] text-fg-muted">
          За период напоминания никому не уходили.
        </div>
      )}
      {r && r.days.length > 0 && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="напоминания о примерке по дням">
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className={`${th} pl-4`} rowSpan={2}>
                    День
                  </th>
                  <th className={`${th} border-r border-border font-normal`} colSpan={4}>
                    Все каналы
                  </th>
                  {channels.map((k, i) => (
                    <th
                      key={k}
                      className={`${th} font-normal ${i + 1 < channels.length ? "border-r border-border" : ""}`}
                      colSpan={4}
                      title={CHANNEL[k]?.hint}
                    >
                      {CHANNEL[k]?.label ?? k}
                    </th>
                  ))}
                </tr>
                <tr>
                  {["all", ...channels].map((k, i) => (
                    <Head key={k} th={th} last={i === channels.length} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.days.map((d) => {
                  const wd = new Date(`${d.day}T12:00:00`).getDay();
                  return (
                    <tr
                      key={d.day}
                      className={`border-t border-border hover:bg-bg-hover ${wd === 0 || wd === 6 ? "bg-bg" : ""}`}
                      data-annot={`день ${d.day}`}
                    >
                      <td className="whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums">
                        {dm(d.day)} {WEEKDAY[wd]}
                      </td>
                      <Cells c={d.total} />
                      {channels.map((k, i) => (
                        <Cells key={k} c={d.channels[k] ?? ZERO} last={i + 1 === channels.length} />
                      ))}
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-border font-medium" data-annot="итого за период">
                  <td className="py-2 pl-4 pr-3 text-fg-muted">
                    Итого {dm(r.from)} – {dm(r.to)}
                  </td>
                  <Cells c={all} />
                  {channels.map((k, i) => (
                    <Cells key={k} c={r.total.channels[k] ?? ZERO} last={i + 1 === channels.length} />
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            День — дата отправки напоминания; запись у клиента — на следующий день, поэтому «Пришли» у сегодняшней
            строки появятся завтра. Напоминаем с 15:00 местного времени салона тем, кто записан на завтра и записался не
            сегодня, только в бесплатные каналы — WhatsApp не шлём. Кнопки «Подтвердить примерку» и «Отменить примерку» есть
            в ботах Telegram и MAX; отмена — в два шага, с вопросом «Вы уверены?». Доли — от отправленных в канал.
          </div>
        </section>
      )}
    </section>
  );
}
